import React from "react";
import {
  Box,
  Typography,
  Divider,
  Container,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import { Gavel as GavelIcon } from "@mui/icons-material";

const LAST_UPDATED = "May 5, 2025";
const COMPANY_NAME = "PresentifyClone Inc.";
const CONTACT_EMAIL = "lag.webservices@gmail.com";
const APP_NAME = "PresentifyClone";
const GOVERNING_LAW = "State of Delaware, United States";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
      {title}
    </Typography>
    {children}
  </Box>
);

const BodyText = ({ children, sx = {} }) => (
  <Typography variant="body2" color="text.secondary" lineHeight={1.8} sx={{ mb: 1.5, ...sx }}>
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ mb: 0.75 }}>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TermsOfService = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
          <GavelIcon color="primary" />
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Terms of Service
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Last updated: ${LAST_UPDATED}`} size="small" variant="outlined" />
          <Chip label={COMPANY_NAME} size="small" color="primary" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8} sx={{ mt: 2 }}>
          Please read these Terms of Service ("Terms") carefully before using the {APP_NAME}
          platform and services operated by {COMPANY_NAME} ("we", "us", or "our"). By
          accessing or using our Services, you agree to be bound by these Terms. If you
          do not agree to these Terms, you may not access or use the Services.
        </Typography>
      </Box>

      {/* Important notice */}
      <Alert
        severity="warning"
        variant="outlined"
        sx={{ mb: 4, borderRadius: 2 }}
      >
        <Typography variant="body2" lineHeight={1.8}>
          <strong>Important:</strong> These Terms contain an arbitration clause and class
          action waiver in Section 17. Please read them carefully as they affect your
          legal rights.
        </Typography>
      </Alert>

      <Divider sx={{ mb: 4 }} />

      {/* 1 */}
      <Section title="1. Acceptance of Terms">
        <BodyText>
          By creating an account, accessing, or using {APP_NAME}, you confirm that you
          are at least 16 years of age, have the legal capacity to enter into a binding
          agreement, and agree to be bound by these Terms and our Privacy Policy. If you
          are using the Services on behalf of an organization, you represent that you have
          the authority to bind that organization to these Terms.
        </BodyText>
        <BodyText>
          We reserve the right to update these Terms at any time. We will provide notice
          of material changes via email or through the Services. Your continued use of the
          Services after changes take effect constitutes your acceptance of the revised Terms.
        </BodyText>
      </Section>

      {/* 2 */}
      <Section title="2. Description of Services">
        <BodyText>
          {APP_NAME} provides a cloud-based software-as-a-service platform that enables
          organizations to manage, track, and analyze operational data. The Services may
          include web applications, mobile applications, APIs, integrations, and related
          documentation (collectively, "Services"). We reserve the right to modify, suspend,
          or discontinue any part of the Services at any time with reasonable notice.
        </BodyText>
      </Section>

      {/* 3 */}
      <Section title="3. Account Registration and Security">
        <BodyText>
          To access most features of our Services, you must register for an account.
          When registering, you agree to:
        </BodyText>
        <BulletList items={[
          "Provide accurate, current, and complete information during registration.",
          "Maintain and promptly update your account information to keep it accurate.",
          "Maintain the confidentiality of your login credentials and not share them with others.",
          "Be responsible for all activity that occurs under your account.",
          "Notify us immediately at " + CONTACT_EMAIL + " of any unauthorized use of your account.",
          "Not create multiple accounts for the purpose of circumventing restrictions or bans.",
        ]} />
        <BodyText>
          We reserve the right to suspend or terminate accounts that we reasonably believe
          have been compromised or are in violation of these Terms.
        </BodyText>
      </Section>

      {/* 4 */}
      <Section title="4. Subscription Plans and Billing">
        <BodyText>
          Access to certain features of the Services requires a paid subscription. By
          subscribing to a paid plan, you agree to pay all applicable fees as described
          at the time of purchase.
        </BodyText>
        <BulletList items={[
          "Subscription fees are billed in advance on a monthly or annual basis depending on your selected plan.",
          "All fees are non-refundable except as expressly stated in our Refund Policy or required by law.",
          "We may change our pricing with at least 30 days' notice prior to your next billing cycle.",
          "Failure to pay fees may result in suspension or termination of your access to the Services.",
          "You are responsible for all applicable taxes associated with your use of the Services.",
          "If you upgrade your plan mid-cycle, you will be charged a prorated amount for the remainder of the billing period.",
        ]} />
      </Section>

      {/* 5 */}
      <Section title="5. Free Trials">
        <BodyText>
          We may offer a free trial period for certain subscription plans. At the end of
          the trial period, your account will automatically convert to a paid subscription
          unless you cancel before the trial expires. We reserve the right to modify or
          terminate free trial offerings at any time without notice.
        </BodyText>
      </Section>

      {/* 6 */}
      <Section title="6. Acceptable Use">
        <BodyText>
          You agree to use the Services only for lawful purposes and in accordance with
          these Terms. You agree not to:
        </BodyText>
        <BulletList items={[
          "Use the Services in any way that violates applicable federal, state, local, or international laws or regulations.",
          "Transmit any material that is defamatory, obscene, fraudulent, or otherwise objectionable.",
          "Impersonate any person or entity or falsely represent your affiliation with any person or entity.",
          "Attempt to gain unauthorized access to any portion of the Services or its related systems.",
          "Use any automated means, bots, scrapers, or data mining tools to access the Services without our express written permission.",
          "Interfere with or disrupt the integrity or performance of the Services or third-party data contained therein.",
          "Upload or transmit viruses, malware, or other malicious code.",
          "Use the Services to store or transmit infringing, libelous, or otherwise unlawful content.",
          "Reverse engineer, decompile, or disassemble any part of the Services.",
          "Resell, sublicense, or otherwise commercialize access to the Services without prior written consent.",
        ]} />
      </Section>

      {/* 7 */}
      <Section title="7. Intellectual Property">
        <BodyText>
          The Services and all content, features, and functionality thereof — including
          but not limited to software, text, graphics, logos, and user interfaces — are
          owned by {COMPANY_NAME} or its licensors and are protected by applicable
          intellectual property laws.
        </BodyText>
        <BodyText>
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
          non-transferable, revocable license to access and use the Services for your internal
          business purposes. This license does not include any rights to:
        </BodyText>
        <BulletList items={[
          "Copy, modify, or create derivative works based on the Services.",
          "Remove or alter any proprietary notices or labels on the Services.",
          "Use {APP_NAME}'s trademarks, logos, or brand features without prior written consent.",
          "Use the Services to build a competing product or service.",
        ]} />
      </Section>

      {/* 8 */}
      <Section title="8. Your Content">
        <BodyText>
          You retain ownership of all data, files, and content you submit to the Services
          ("Your Content"). By submitting Your Content, you grant us a worldwide, royalty-free,
          non-exclusive license to use, store, process, and display Your Content solely for the
          purpose of providing the Services to you.
        </BodyText>
        <BodyText>
          You represent and warrant that you have all necessary rights to submit Your Content
          and that Your Content does not violate any third-party rights or applicable laws.
          We are not responsible for the accuracy, completeness, or legality of Your Content.
        </BodyText>
      </Section>

      {/* 9 */}
      <Section title="9. Data Privacy">
        <BodyText>
          Your use of the Services is also governed by our Privacy Policy, which is
          incorporated into these Terms by reference. By using the Services, you consent
          to the collection, use, and sharing of your information as described in our
          Privacy Policy. For enterprise customers, the terms of any applicable Data
          Processing Agreement will govern the processing of personal data.
        </BodyText>
      </Section>

      {/* 10 */}
      <Section title="10. Third-Party Services and Integrations">
        <BodyText>
          The Services may integrate with or contain links to third-party services,
          websites, or applications. We do not control and are not responsible for the
          content, privacy policies, or practices of third-party services. Your use of
          any third-party service is subject to that service's own terms and conditions.
          We encourage you to review the terms and privacy policies of any third-party
          services you use in connection with {APP_NAME}.
        </BodyText>
      </Section>

      {/* 11 */}
      <Section title="11. Confidentiality">
        <BodyText>
          Each party agrees to keep confidential all non-public information disclosed by
          the other party in connection with the Services that is designated as confidential
          or that reasonably should be understood to be confidential given the nature of the
          information and the circumstances of disclosure. This obligation does not apply
          to information that is publicly available, independently developed, or required
          to be disclosed by law.
        </BodyText>
      </Section>

      {/* 12 */}
      <Section title="12. Disclaimers and Warranties">
        <BodyText>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY
          KIND. TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} EXPRESSLY
          DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT.
        </BodyText>
        <BodyText>
          We do not warrant that the Services will be uninterrupted, error-free, or
          completely secure. We do not warrant that any defects in the Services will be
          corrected or that the Services are free of viruses or other harmful components.
        </BodyText>
      </Section>

      {/* 13 */}
      <Section title="13. Limitation of Liability">
        <BodyText>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME.toUpperCase()} AND
          ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR
          ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
          BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES,
          ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICES.
        </BodyText>
        <BodyText>
          IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
          RELATED TO THESE TERMS OR THE SERVICES EXCEED THE GREATER OF (A) THE AMOUNTS
          YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
          DOLLARS ($100).
        </BodyText>
      </Section>

      {/* 14 */}
      <Section title="14. Indemnification">
        <BodyText>
          You agree to indemnify, defend, and hold harmless {COMPANY_NAME} and its
          officers, directors, employees, agents, and licensors from and against any
          claims, liabilities, damages, judgments, awards, losses, costs, expenses, or
          fees (including reasonable attorneys' fees) arising out of or relating to:
        </BodyText>
        <BulletList items={[
          "Your violation of these Terms.",
          "Your use of the Services in a manner not authorized by these Terms.",
          "Your Content or any data you submit to the Services.",
          "Your violation of any third-party rights, including intellectual property rights.",
          "Your violation of any applicable law or regulation.",
        ]} />
      </Section>

      {/* 15 */}
      <Section title="15. Term and Termination">
        <BodyText>
          These Terms remain in effect for as long as you use the Services. We may suspend
          or terminate your access to the Services at any time, with or without cause,
          with or without notice, including if we reasonably believe you have violated
          these Terms.
        </BodyText>
        <BodyText>
          Upon termination, your right to use the Services will immediately cease. You
          may export Your Content prior to termination. Following termination, we may
          retain Your Content for up to 30 days before permanent deletion, unless otherwise
          required by law.
        </BodyText>
      </Section>

      {/* 16 */}
      <Section title="16. Governing Law">
        <BodyText>
          These Terms shall be governed by and construed in accordance with the laws of
          the {GOVERNING_LAW}, without regard to its conflict of law provisions. Any
          disputes not subject to arbitration under Section 17 shall be resolved exclusively
          in the state or federal courts located in Delaware, and you consent to the
          personal jurisdiction of such courts.
        </BodyText>
      </Section>

      {/* 17 */}
      <Section title="17. Dispute Resolution and Arbitration">
        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" lineHeight={1.8}>
            Please read this section carefully. It requires you to arbitrate disputes
            and limits the manner in which you can seek relief from {COMPANY_NAME}.
          </Typography>
        </Alert>
        <BodyText>
          Any dispute, controversy, or claim arising out of or relating to these Terms
          or the Services shall be resolved by binding arbitration administered by the
          American Arbitration Association (AAA) under its Commercial Arbitration Rules.
          The arbitration shall be conducted in English in Delaware.
        </BodyText>
        <BodyText>
          <strong>Class Action Waiver:</strong> You and {COMPANY_NAME} agree that any
          arbitration shall be conducted on an individual basis only, and not as a class,
          consolidated, or representative action. You waive any right to bring or
          participate in a class action lawsuit.
        </BodyText>
        <BodyText>
          Notwithstanding the foregoing, either party may seek injunctive or other
          equitable relief in any court of competent jurisdiction to prevent actual or
          threatened infringement of intellectual property rights.
        </BodyText>
      </Section>

      {/* 18 */}
      <Section title="18. General Provisions">
        <BulletList items={[
          "Entire Agreement: These Terms, together with our Privacy Policy and any applicable Order Forms, constitute the entire agreement between you and " + COMPANY_NAME + " regarding the Services.",
          "Severability: If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.",
          "Waiver: Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.",
          "Assignment: You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.",
          "Force Majeure: We will not be liable for delays or failures in performance resulting from events beyond our reasonable control.",
          "Notices: All legal notices to us must be sent to " + CONTACT_EMAIL + " and will be deemed received upon acknowledgment by us.",
        ]} />
      </Section>

      {/* 19 */}
      <Section title="19. Contact Information">
        <BodyText>
          If you have any questions about these Terms, please contact our legal team:
        </BodyText>
        <Box
          sx={{
            p: 2.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {COMPANY_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Legal & Compliance Team
          </Typography>
          <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
            {CONTACT_EMAIL}
          </Typography>
        </Box>
      </Section>

      <Divider sx={{ mt: 2, mb: 3 }} />
      <Typography variant="caption" color="text.disabled">
        © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
      </Typography>
    </Container>
  );
};

export default TermsOfService;